import { defaultPreferences, inferTemplate, isActive, templates } from '../catalog';
import {
  briefSchema,
  snapshotSchema,
  type ReportJob,
  type ReportsGateway,
  type ReportSnapshot,
} from '../types';
import { fixtureBlocks } from './fixture';

const key = (scope: string) => `arbitralis:demo-reports:v1:${scope}`;
const memory = new Map<string, ReportSnapshot>();
function read(scope: string): ReportSnapshot {
  try {
    const raw = localStorage.getItem(key(scope));
    if (raw) {
      const parsed = snapshotSchema.safeParse(JSON.parse(raw));
      if (parsed.success) return parsed.data;
    }
  } catch {
    /* Storage privado: sessão em memória. */
  }
  return memory.get(scope) ?? { jobs: [], preferences: defaultPreferences };
}
function write(scope: string, snapshot: ReportSnapshot) {
  memory.set(scope, snapshot);
  try {
    localStorage.setItem(key(scope), JSON.stringify(snapshot));
  } catch {
    /* Apenas dados fictícios; sem bloquear o fluxo. */
  }
}
export function advanceDemoJob(job: ReportJob, now = Date.now()): ReportJob {
  if (!isActive(job.status)) return job;
  const elapsed = Math.max(0, now - Date.parse(job.createdAt));
  const stage = Math.min(4, Math.floor(elapsed / 3200));
  const completed = elapsed >= 18000;
  const count = completed
    ? job.brief.blocks.length
    : Math.max(0, Math.floor((elapsed - 7200) / 1400));
  return {
    ...job,
    version: Math.floor(elapsed / 1000),
    stage,
    updatedAt: new Date(now).toISOString(),
    status: completed ? 'completed' : 'running',
    blocks: job.brief.blocks
      .slice(0, count)
      .map((id) => fixtureBlocks.find((block) => block.id === id)!)
      .filter(Boolean),
  };
}
function update(scope: string, id: string, cancel = false) {
  const snapshot = read(scope);
  const job = snapshot.jobs.find((item) => item.id === id);
  if (!job) throw new Error('Relatório não encontrado neste espaço.');
  const updated =
    cancel && isActive(job.status)
      ? { ...job, status: 'cancelled' as const, version: job.version + 1 }
      : advanceDemoJob(job);
  write(scope, {
    ...snapshot,
    jobs: snapshot.jobs.map((item) => (item.id === id ? updated : item)),
  });
  return updated;
}
export const demoGateway: ReportsGateway = {
  async interpret(_scope, text) {
    const template = inferTemplate(text) ?? templates[0];
    const supported =
      !/piada|receita de|programar|senha|ignore.*regras|delete.*from|temperatura|previs[aã]o do tempo|traduzir|futebol/i.test(
        text,
      );
    return {
      supported,
      title: template.title,
      focus: template.id,
      blocks: template.blocks,
      summary: supported
        ? `Você quer um relatório de negociação com este foco: “${text.slice(0, 600)}”. Vou organizar os indicadores nos blocos abaixo e considerar os detalhes que você trouxe. É isso que você precisa?`
        : 'Eu cuido de relatórios e dashboards de negociação. Posso analisar resultados, comparar recortes e explicar indicadores. O que você gostaria de entender sobre suas negociações?',
    };
  },
  async load(scope) {
    const snapshot = read(scope);
    const next = { ...snapshot, jobs: snapshot.jobs.map((job) => advanceDemoJob(job)) };
    write(scope, next);
    return next;
  },
  async create(scope, rawBrief, id) {
    const brief = briefSchema.parse(rawBrief);
    const snapshot = read(scope);
    const existing = snapshot.jobs.find((job) => job.id === id);
    if (existing) return existing;
    if (snapshot.jobs.some((job) => isActive(advanceDemoJob(job).status)))
      throw new Error('Aguarde o relatório em construção antes de iniciar outro.');
    const now = new Date().toISOString();
    const job: ReportJob = {
      id,
      workspaceId: scope,
      version: 0,
      createdAt: now,
      updatedAt: now,
      status: 'queued',
      stage: 0,
      brief,
      blocks: [],
      demo: true,
      notificationEnabled: false,
    };
    write(scope, { ...snapshot, jobs: [job, ...snapshot.jobs].slice(0, 50) });
    return job;
  },
  async get(scope, id) {
    return update(scope, id);
  },
  async cancel(scope, id) {
    return update(scope, id, true);
  },
  async preferences(scope, preferences) {
    write(scope, { ...read(scope), preferences });
    return preferences;
  },
};
